import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit2, Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/components/AuthProvider';
import { getUsersFromEmpresa, deleteOperador, updateOperadorStatus } from '@/integrations/supabase/permissions';
import { DialogNovoOperador } from '@/components/DialogNovoOperador';
import { DialogEditarPermissoes } from '@/components/DialogEditarPermissoes';
import { UsersBlockedUpgrade } from '@/components/UsersBlockedUpgrade';
import { getPlanInfo } from '@/integrations/supabase/plan-utils';
import { OperadorInfo } from '@/types/permissions';
import { toast } from 'sonner';

export function UsuariosPermissoes() {
  const navigate = useNavigate();
  const { user, empresa } = useAuth();
  const empresaId = empresa?.id;
  const queryClient = useQueryClient();
  const [novoOperadorOpen, setNovoOperadorOpen] = useState(false);
  const [editandoOperador, setEditandoOperador] = useState<OperadorInfo | null>(null);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [planInfo, setPlanInfo] = useState<any>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);

  // Verificar plano do usuário
  useEffect(() => {
    if (empresaId) {
      getPlanInfo(empresaId).then(info => {
        setPlanInfo(info);
        setIsCheckingAccess(false);
      });
    }
  }, [empresaId]);

  const { data: operadores = [], isLoading } = useQuery({
    queryKey: ['operadores', empresaId],
    queryFn: () => (empresaId ? getUsersFromEmpresa(empresaId) : Promise.resolve([])),
    enabled: !!empresaId
  });

  async function handleDeleteOperador(operadorId: string, isOwner: boolean) {
    if (isOwner) {
      toast.error('Não é possível deletar o proprietário da empresa');
      return;
    }

    if (!window.confirm('Tem certeza que deseja deletar este operador?')) return;

    try {
      await deleteOperador(operadorId);
      queryClient.invalidateQueries({ queryKey: ['operadores', empresaId] });
      toast.success('Operador removido com sucesso');
    } catch (error) {
      toast.error('Erro ao remover operador');
      console.error(error);
    }
  }

  async function handleToggleStatus(userEmpresaId: string, currentStatus: string) {
    const newStatus = currentStatus === 'ativo' ? 'inativo' : 'ativo';
    try {
      await updateOperadorStatus(userEmpresaId, newStatus as 'ativo' | 'inativo');
      queryClient.invalidateQueries({ queryKey: ['operadores', empresaId] });
      toast.success(`Operador ${newStatus === 'ativo' ? 'ativado' : 'desativado'}`);
    } catch (error) {
      toast.error('Erro ao atualizar status');
      console.error(error);
    }
  }

  // Se estiver verificando acesso, mostrar loading
  if (isCheckingAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // ⚠️ FUNCIONALIDADE EM DESENVOLVIMENTO - Bloqueado temporariamente
  const isFeatureDisabled = true;

  // Nota: Plano Básico permite 1 operador, Premium permite 3
  // Nenhum bloqueio aqui, o DialogNovoOperador verifica o limite

  return (
    <div className="px-6 py-6 space-y-6 relative">
      {/* ⚠️ Overlay de funcionalidade em desenvolvimento */}
      {isFeatureDisabled && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 rounded-lg">
          <Card className="w-full max-w-md bg-white shadow-2xl border-2 border-orange-500">
            <CardHeader className="text-center space-y-2">
              <div className="text-4xl mb-2">⏳</div>
              <CardTitle className="text-2xl text-orange-600">Em Desenvolvimento</CardTitle>
              <CardDescription className="text-base text-gray-700 pt-2">
                Esta funcionalidade está sendo aprimorada e será lançada em breve!
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-sm text-gray-600">
                Estamos trabalhando para melhorar a experiência de gerenciamento de operadores e permissões.
              </p>
              <Button
                onClick={() => navigate('/configuracoes')}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                Voltar para Configurações
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
      <div className="space-y-2 mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/configuracoes')}
          className="text-purple-600 hover:bg-purple-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
      </div>
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Usuários e Permissões</h1>
          <p className="text-gray-600">Gerencie operadores e suas permissões</p>
        </div>
        <Button onClick={() => setNovoOperadorOpen(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Novo Operador
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : operadores.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Nenhum operador registrado</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {operadores.map((operador) => (
            <Card key={operador.id} className="overflow-hidden">
              <div className="flex items-center justify-between p-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div>
                      <h3 className="font-semibold text-lg">{operador.full_name}</h3>
                      <p className="text-sm text-gray-500">{operador.email}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Badge variant="outline">{operador.role === 'admin' ? 'Administrador' : 'Operador'}</Badge>
                    <Badge variant={operador.status === 'ativo' ? 'default' : 'secondary'}>
                      {operador.status === 'ativo' ? 'Ativo' : 'Inativo'}
                    </Badge>
                    {operador.is_owner && <Badge className="bg-blue-500">Proprietário</Badge>}
                  </div>
                </div>

                <div className="flex gap-2">
                  {!operador.is_owner && operador.role !== 'admin' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditandoOperador(operador)}
                      className="gap-2"
                    >
                      <Edit2 className="w-4 h-4" />
                      Permissões
                    </Button>
                  )}

                  {!operador.is_owner && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleStatus(operador.id, operador.status)}
                      className="gap-2"
                    >
                      {operador.status === 'ativo' ? (
                        <>
                          <EyeOff className="w-4 h-4" />
                          Desativar
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4" />
                          Ativar
                        </>
                      )}
                    </Button>
                  )}

                  {!operador.is_owner && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteOperador(operador.id, operador.is_owner)}
                      className="gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Deletar
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <DialogNovoOperador open={novoOperadorOpen} onOpenChange={setNovoOperadorOpen} />

      {editandoOperador && (
        <DialogEditarPermissoes
          operador={editandoOperador}
          onClose={() => setEditandoOperador(null)}
        />
      )}
    </div>
  );
}
